import { Environment, getEnvironment } from './utils/utils';

const EnvironmentMapping: Record<Environment, string> = {
  Development: 'https://apis-d.generalmills.com',
  QA: 'https://apis-q.generalmills.com',
  Production: 'https://apis.generalmills.com',
};

// Returns the base URL for GMI APIs based on the current settings for the requested service
function getGmiServiceBaseUrl(overrideEnvironment?: Environment) {
  const environment = overrideEnvironment ?? getEnvironment();
  const baseUrl = EnvironmentMapping[environment];

  return baseUrl;
}

export function getRedirectsApiUrl(siteId: string) {
  const baseUrl = getGmiServiceBaseUrl();
  const limit = 100000;
  const REDIRECTS_API_KEY = process.env.REDIRECTS_API_KEY;
  return `${baseUrl}/marketing/coresites/siteredirect/v2/sites/${siteId}/redirects?apikey=${REDIRECTS_API_KEY}&limit=${limit}`;
}

// Gets the base bearer token URL with API service setting environment
export function getBearerTokenUrl(siteId: string) {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/${siteId}/oauth/access_token`;
}

// Gets the base product service URL with API service setting environment
export function getProductServiceUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/productservice/v1/products`;
}

// Gets the base product service URL with API service setting environment
export function getProductServiceUrlV3() {
  // TODO: Remove this once we have a production environment
  // Per Michael O'Neill, we need to use the QA environment for the product service URL until we have a production environment
  const baseUrl = getGmiServiceBaseUrl('QA');
  return `${baseUrl}/marketing/platform/productservice/v3/products`;
}

// Gets the base content service URL with API service setting environment
export function getContentServiceUrl(siteId: string) {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/contentservice/v1/${siteId}/content`;
}

// Gets the base content service URL with API service setting environment
export function getSearchHintsServiceUrl(siteId: string) {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/contentservice/v1/${siteId}/Hints`;
}

// Gets the base profile URL with API service setting environment
export function getProfileUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1`;
}

// Gets the base profile update URL with API service setting environment
export function getProfileUpdateUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/users/me`;
}

// Gets the base metadata service URL with API service setting environment
export function getMetadataServiceUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/metadataservice/v1/values`;
}

// Gets the Authorize (assume identity) URL to fetch Bearer Token with API service setting environment
export function getAuthorizeTokenUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/oauth/identity/assume`;
}

export function getScheduleEmailServiceUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/emailservice/v1/schedule`;
}

export function getFavoritesServiceUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/favoritesservice/v1/favorites`;
}

export function getFavoritesLabelsServiceUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/favoritesservice/v1/labels`;
}

// Gets the password reset token URL with API service setting environment
export function getPasswordResetTokenUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/users/reset-password/get-token`;
}

// Gets the password reset URL with API service setting environment
export function getPasswordResetUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/users/reset-password`;
}

// Gets the send password reset URL with API service setting environment
export function getSendPasswordResetUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/users/send-password-reset`;
}

// Gets User activity based on user email
export function getUserActivityUrl(email: string) {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/users/activity?Email=${email}`;
}

// Gets current user detail based on user id
export function getCurrentUserDetailsUrl(userId: string) {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/users/${userId}`;
}

// Get subscription by guid url.
export function getSubscriptionsByGuidUrl(guid: string) {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/metadata/subscriptions/${guid}`;
}

// Gets the unsubscribe url (uses the /users endpoint with Subscriptions payload)
export function getUnsubscribeUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/users`;
}

// Gets user status based on email (returns UserId and Status: 'Email' | 'User')
export function getUserStatusUrl(email: string) {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/users/status?Email=${encodeURIComponent(email)}`;
}

// Gets the update user url.
export function getUserUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/users/me`;
}

export function getSuppressEmailUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/suppression/suppress-email`;
}

export function getSuppressUserUrl() {
  const baseUrl = getGmiServiceBaseUrl();
  return `${baseUrl}/marketing/platform/profile/v1/suppression/suppress-user`;
}
